/**
 * assets/collection-load-more.js
 * Vanilla load-more for collection grids.
 *
 * Wires up every [data-load-more] button on the page.
 * On click:
 *   1. Fetches the next page URL (data-next-url).
 *   2. Parses the response HTML, extracts grid items.
 *   3. Appends items to the existing grid (data-grid-id).
 *   4. Replaces the pagination zone (data-pagination-id) with
 *      the parsed node from the fetched page — no innerHTML.
 *   5. Updates the shown-count text via aria-live.
 *   6. Restores focus to the first newly-appended card link.
 *
 * No dependencies. No build step. Defer-loaded.
 */

(function () {
  'use strict';

  /**
   * Initialise all load-more buttons present in the DOM.
   * Called on DOMContentLoaded and after each load-more replaces
   * the pagination zone (so the new button is also wired up).
   */
  function initLoadMoreButtons() {
    document.querySelectorAll('[data-load-more]').forEach(function (btn) {
      if (btn.dataset.loadMoreBound) return;
      btn.dataset.loadMoreBound = 'true';
      btn.addEventListener('click', handleLoadMore);
    });
  }

  /**
   * @param {MouseEvent} event
   */
  function handleLoadMore(event) {
    var btn = event.currentTarget;
    var nextUrl = btn.dataset.nextUrl;
    var gridId = btn.dataset.gridId;
    var paginationId = btn.dataset.paginationId;

    if (!nextUrl || !gridId) return;

    var grid = document.getElementById(gridId);
    var paginationZone = document.getElementById(paginationId);

    if (!grid) return;

    btn.setAttribute('aria-disabled', 'true');
    btn.disabled = true;

    fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Extract new grid items from the fetched page
        var newGrid = doc.getElementById(gridId);
        if (!newGrid) return;

        var newItems = Array.from(newGrid.querySelectorAll('.collection__grid-item'));
        var firstNewItem = null;

        newItems.forEach(function (item, idx) {
          var clone = item.cloneNode(true);
          grid.appendChild(clone);
          if (idx === 0) firstNewItem = clone;
        });

        // Replace pagination zone with the new page's pagination zone (DOM node, not innerHTML)
        if (paginationZone) {
          var newPaginationZone = doc.getElementById(paginationId);
          if (newPaginationZone) {
            // Import the node from the parsed document into the live document
            var imported = document.adoptNode(newPaginationZone);
            paginationZone.replaceWith(imported);
            // Re-init buttons in the new pagination element
            initLoadMoreButtons();
          } else {
            // No more pages — remove just the load-more button, keep the count text
            var oldBtn = paginationZone.querySelector('[data-load-more]');
            if (oldBtn) oldBtn.remove();
          }
        }

        // Focus first newly appended card for keyboard accessibility
        if (firstNewItem) {
          var firstLink = firstNewItem.querySelector('a');
          if (firstLink) {
            firstLink.setAttribute('tabindex', '-1');
            firstLink.focus({ preventScroll: true });
            firstLink.removeAttribute('tabindex');
          }
        }
      })
      .catch(function (err) {
        console.error('[collection-load-more]', err);
        // Re-enable on error so user can retry
        btn.removeAttribute('aria-disabled');
        btn.disabled = false;
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLoadMoreButtons);
  } else {
    initLoadMoreButtons();
  }
})();
